import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";

import { useRouteSlug } from "@/hooks";
import SuccessGIF from "@/images/successfully.gif";
import UpdateDetailsComponent from "@/components/updateDetails";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { updateUser } from "@/redux/actions/myAccount.action";
import { selectGetResponseUpdateDetails } from "@/util/selectors";
import { resetResponseUpdateDetailsData } from "@/redux/actions/authConfig.action";
import { useHistory, useLocation } from "react-router-dom";
import AccountAnalyticsEvent from "@/services/analytics/main/myAccount";
import AnalyticService from "@/services/analytic-service/AnalyticService";

import "./style.scss";
import AuthAnaltyicsEvent from "@/services/analytics/main/authentication";
import { normalizePathFromQuery } from "@/utils";

type MissedField = {
  enabled: boolean;
  mandatory: boolean;
};

export type DynamicUpdateProfileFields = {
  firstname?: MissedField;
  email?: MissedField;
  phone?: MissedField;
  gender?: MissedField;
  country?: MissedField;
  birthday?: MissedField;
  nationality?: MissedField;
  password?: MissedField;
};

const UpdateDetails = ({ isMobile = false }) => {
  const [missedFields, setMissedFields] =
    useState<DynamicUpdateProfileFields>(null);
  const [token, setToken] = useState("");
  const [userProfile, setUserProfile] = useState({});
  const [showButtonLoader, setShowButtonLoader] = useState(false);
  const { t } = useTranslation("auth");
  const dispatch = useAppDispatch();
  const responseUpdateDetails = useAppSelector(selectGetResponseUpdateDetails);
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const analyticsData = useAppSelector(state => state.homepage.analyticsData);
  const { search } = useLocation();

  const redirect = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const checkoutRedirect = searchParams.get("redirect");
    return checkoutRedirect;
  }, [location.search]);

  const redirectParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("redirect") || "";
    return normalizePathFromQuery(raw);
  }, [location.search]);

  const isCartRedirect = useMemo(() => {
    const p = redirectParam.replace(/\/+$/, "");
    return p === "my-cart";
  }, [redirectParam]);

  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    const profile = responseUpdateDetails?.userProfile || {};
    if (!profile) return;
    AuthAnaltyicsEvent.signUp({
      isSocialLogin: true,
      customerId: profile.customerId,
      registerData: profile,
      method: analyticsData?.loginFrom ?? ""
    });

    didRun.current = true;
  }, [responseUpdateDetails?.userProfile]);

  const handleUpdateUserDetails =
    ({ bodyData, token }) =>
    async (dispatch, getState) => {
      try {
        setShowButtonLoader(true);
        const { isNewsLetter, isLoyaltyProgramChecked, ...dataFromForm } =
          bodyData;

        if (isEmpty(dataFromForm)) return;
        const changes = Object.keys(dataFromForm);

        const updatedDetails = {
          ...userProfile,
          ...bodyData,
          changes
        };

        const resp = await dispatch(updateUser(updatedDetails));
        //@ts-ignore
        if (resp.data?.success) {
          AccountAnalyticsEvent.accountInfoSaved();
          AnalyticService.auth.trackProfileUpdate({
            updatedMyDetails: updatedDetails,
            customerId: updatedDetails?.customerId
          });
          dispatch(resetResponseUpdateDetailsData());
          history.push(`/${routeSlug}/${redirect ?? ""}`);
        }
      } catch (error) {
        console.error(`Update details error: ${error}`);
      } finally {
        setShowButtonLoader(false);
      }
    };

  const onAuthMissingFieldsError = response => {
    if (response.requiredFields || response.optionalFields) {
      const requiredFields = response.requiredFields?.reduce((acc, key) => {
        acc[key] = { enabled: true, mandatory: true };
        return acc;
      }, {});
      const optionalResult = response.optionalFields?.reduce((acc, key) => {
        acc[key] = { enabled: true };
        return acc;
      }, {});
      setMissedFields({ ...optionalResult, ...requiredFields });
      setToken(response.token);
      setUserProfile(response.userProfile);
    }
  };

  useEffect(() => {
    if (responseUpdateDetails) {
      onAuthMissingFieldsError(responseUpdateDetails);
    }
  }, [responseUpdateDetails]);

  useEffect(() => {
    if (!responseUpdateDetails) {
      history.push(`/${routeSlug}/`);
    }
  }, []);

  return (
    <div className="successContainer">
      {isMobile && (
        <div className="gifWrapper">
          <img src={SuccessGIF} alt="success gif" className="successGif" />
        </div>
      )}
      <h2 className="title">{t("registered")}</h2>
      <p className="greySubtitle">{t("accountCreated")}</p>
      <p className="shareDetails">{t("shareMoreDetails")}</p>
      <UpdateDetailsComponent
        data={missedFields}
        token={token}
        updateAction={handleUpdateUserDetails}
        userProfile={userProfile}
        className="updateOptionalData"
        isSignUpFlow
        isSubmitting={showButtonLoader}
        socialFlow
        isUserUpdateFlow
      />
    </div>
  );
};

export default UpdateDetails;
